import { SignalingClient, ServerToClientMessage } from '../signaling';

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export interface StreamerCallbacks {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReconnecting?: () => void;
  onError?: (error: Error) => void;
}

export class StreamerConnection {
  private eventId: string;
  private mediaStream: MediaStream;
  private signaling: SignalingClient;
  private pc: RTCPeerConnection | null = null;
  private callbacks: StreamerCallbacks;
  private streamId: string | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private label: string;

  constructor(eventId: string, mediaStream: MediaStream, callbacks: StreamerCallbacks = {}) {
    this.eventId = eventId;
    this.mediaStream = mediaStream;
    this.callbacks = callbacks;
    this.label = `Streamer ${Date.now().toString(36)}`;
    console.log('[Streamer] Initializing streamer for event:', eventId, 'label:', this.label);

    this.signaling = new SignalingClient(eventId, {
      onMessage: (msg) => this.handleSignalingMessage(msg),
      onConnected: () => {
        console.log('[Streamer] Signaling connected, sending join message');
        this.signaling.send({ type: 'join', role: 'streamer', label: this.label });
      },
      onDisconnected: () => {
        console.log('[Streamer] Signaling disconnected');
        this.callbacks.onReconnecting?.();
      },
      onError: (error) => {
        console.error('[Streamer] Signaling error:', error);
        this.callbacks.onError?.(error);
      },
    });
  }

  connect(): void {
    console.log('[Streamer] Connecting to event:', this.eventId);
    this.signaling.connect();
  }

  disconnect(): void {
    this.signaling.disconnect();
    this.pc?.close();
    this.pc = null;
    this.streamId = null;
    this.callbacks.onDisconnected?.();
  }

  private handleSignalingMessage(message: ServerToClientMessage): void {
    console.log('[Streamer] Handling signaling message:', message.type);
    switch (message.type) {
      case 'joined':
        this.streamId = message.streamId || null;
        console.log('[Streamer] Joined with streamId:', this.streamId);
        break;
      case 'request-offer':
        console.log('[Streamer] Received request-offer');
        this.createAndSendOffer();
        break;
      case 'answer':
        console.log('[Streamer] Received answer');
        this.handleAnswer(message.sdp);
        break;
      case 'ice-candidate':
        console.log('[Streamer] Received ICE candidate');
        this.handleIceCandidate(message.candidate);
        break;
    }
  }

  private async createAndSendOffer(): Promise<void> {
    console.log('[Streamer] Creating and sending offer');
    try {
      // Close existing peer connection if any
      this.pc?.close();

      // Create new peer connection
      console.log('[Streamer] Creating peer connection');
      this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      this.pendingCandidates = [];

      // Add tracks from media stream
      const tracks = this.mediaStream.getTracks();
      console.log('[Streamer] Adding', tracks.length, 'tracks to peer connection');
      tracks.forEach((track) => {
        console.log('[Streamer] Adding track:', track.kind, track.label);
        this.pc!.addTrack(track, this.mediaStream);
      });

      // Handle ICE candidates
      this.pc.onicecandidate = (event) => {
        if (event.candidate && this.streamId) {
          console.log('[Streamer] Sending ICE candidate');
          this.signaling.send({
            type: 'ice-candidate',
            candidate: event.candidate.toJSON(),
            streamId: this.streamId,
          });
        }
      };

      // Handle connection state changes
      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState;
        console.log('[Streamer] Connection state changed:', state);
        if (state === 'connected') {
          this.callbacks.onConnected?.();
        } else if (state === 'disconnected' || state === 'failed') {
          this.callbacks.onReconnecting?.();
        }
      };

      // Create and send offer
      console.log('[Streamer] Creating offer');
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      console.log('[Streamer] Sending offer');
      this.signaling.send({
        type: 'offer',
        sdp: this.pc.localDescription!,
      });
    } catch (error) {
      console.error('[Streamer] Failed to create offer:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  private async handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    console.log('[Streamer] Handling answer');
    if (!this.pc) {
      console.error('[Streamer] No peer connection exists');
      return;
    }

    try {
      console.log('[Streamer] Setting remote description');
      await this.pc.setRemoteDescription(new RTCSessionDescription(sdp));

      // Flush pending ICE candidates
      console.log('[Streamer] Flushing', this.pendingCandidates.length, 'pending ICE candidates');
      for (const candidate of this.pendingCandidates) {
        await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
      }
      this.pendingCandidates = [];
    } catch (error) {
      console.error('[Streamer] Failed to handle answer:', error);
    }
  }

  private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc) return;

    try {
      if (this.pc.remoteDescription) {
        await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        // Buffer candidate until remote description is set
        this.pendingCandidates.push(candidate);
      }
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }
}
